import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import axios from "axios";
import { Reveal, SectionHeading } from "./Reveal";
import { CORPORATE } from "../../lib/data";

const API = process.env.REACT_APP_BACKEND_URL;

// Corporate + Concierge presented in one horizontal row at every viewport
// (down to 360px). Each card is entirely clickable and preserves its
// existing route:
//   Corporate  → /corporate
//   Concierge  → /contact?category=concierge  (form preselects the
//                custom-transportation service and 'Concierge & Special
//                Requests' subject — see Contact.jsx).
export default function CorporateConcierge() {
  const navigate = useNavigate();
  const [corporateEnabled, setCorporateEnabled] = useState(false);

  useEffect(() => {
    let active = true;

    axios.get(`${API}/api/corporate/status`)
      .then((response) => {
        if (active) {
          setCorporateEnabled(response.data?.enabled === true);
        }
      })
      .catch(() => {
        if (active) {
          setCorporateEnabled(false);
        }
      });

    return () => {
      active = false;
    };
  }, []);

  const goCorp = () => {
    if (corporateEnabled) {
      navigate("/corporate");
    }
  };

  const goConc = () => navigate("/contact?category=concierge");

  return (
    <section
      id="corporate"
      data-testid="corporate-concierge-section"
      className="px-3 sm:px-4 pt-6 sm:pt-10"
    >
      <div className="mx-auto max-w-6xl">
        <div
          className="grid grid-cols-2 gap-2 sm:gap-4 lg:gap-6 items-stretch"
          data-testid="corporate-concierge-grid"
        >
          {/* ---------- Corporate Solutions -------------------------------- */}
          <Reveal delay={0.05}>
            <button
              type="button"
              onClick={goCorp}
              disabled={!corporateEnabled}
              aria-disabled={!corporateEnabled}
              aria-label={
                corporateEnabled
                  ? "Open the VOOR BLACK Corporate portal"
                  : "VOOR BLACK Corporate Solutions — Coming Soon"
              }
              data-testid="corporate-section"
              className={`cc-card group relative w-full h-full text-left overflow-hidden ${
                corporateEnabled ? "" : "cc-card-disabled"
              }`}
              style={{
                borderRadius: 16,
                minWidth: 0,
              }}
            >
              <div
                data-testid="corporate-solutions"
                className="relative w-full h-full flex flex-col"
                style={{
                  minHeight: "clamp(220px, 34vw, 360px)",
                  border: "1px solid rgba(214,212,0,0.35)",
                  borderRadius: 16,
                  overflow: "hidden",
                  backgroundImage:
                    "linear-gradient(180deg, rgba(5,5,5,0.35) 0%, rgba(5,5,5,0.82) 100%), url('/landing/corporate-hero.jpg')",
                  backgroundSize: "cover",
                  backgroundPosition: "center",
                  backgroundRepeat: "no-repeat",
                }}
              >
                <div className="flex-1 p-3 sm:p-6 lg:p-8 flex flex-col justify-end">
                  <SectionHeading testId="corporate-heading">Corporate Solutions</SectionHeading>
                  <div className="hidden sm:flex flex-col gap-1.5 mt-3">
                    {CORPORATE.slice(0, 4).map((c) => (
                      <span key={c} className="font-body text-[#c9c9ba] text-[11px] lg:text-sm leading-snug">
                        {c}
                      </span>
                    ))}
                  </div>
                  <p
                    className="font-body text-[#c9c9ba] sm:hidden leading-snug mt-2"
                    style={{ fontSize: "12px" }}
                  >
                    Enterprise accounts, dedicated dispatch, transparent billing.
                  </p>
                  <span
                    className="inline-flex items-center gap-1.5 mt-3 sm:mt-5 font-display font-bold uppercase tracking-[0.14em] text-[#d6d400]"
                    style={{ fontSize: "clamp(10px, 1vw, 13px)" }}
                    data-testid="corporate-callout"
                  >
                    <span>
                      {corporateEnabled ? "View Corporate Services" : "Coming Soon"}
                    </span>
                    {corporateEnabled && (
                      <ArrowRight size={13} strokeWidth={2} />
                    )}
                  </span>
                </div>
              </div>
            </button>
          </Reveal>

          {/* ---------- Concierge & Special Requests ----------------------- */}
          <Reveal delay={0.1}>
            <button
              type="button"
              onClick={goConc}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  goConc();
                }
              }}
              aria-label="Contact the VOOR BLACK concierge"
              data-testid="concierge-section"
              className="cc-card group relative w-full h-full text-left overflow-hidden"
              style={{
                borderRadius: 16,
                minWidth: 0,
              }}
            >
              <div
                className="relative w-full h-full flex flex-col"
                style={{
                  minHeight: "clamp(220px, 34vw, 360px)",
                  border: "1px solid rgba(214,212,0,0.35)",
                  borderRadius: 16,
                  overflow: "hidden",
                  backgroundImage:
                    "linear-gradient(180deg, rgba(5,5,5,0.35) 0%, rgba(5,5,5,0.82) 100%), url('/landing/concierge-hero.jpg')",
                  backgroundSize: "cover",
                  backgroundPosition: "center",
                  backgroundRepeat: "no-repeat",
                }}
              >
                <div className="flex-1 p-3 sm:p-6 lg:p-8 flex flex-col justify-end">
                  <h3
                    className="font-display font-bold uppercase tracking-[0.12em] text-[#f4f4ea] leading-tight"
                    style={{ fontSize: "clamp(16px, 2.4vw, 30px)" }}
                  >
                    <span className="hidden sm:inline">Concierge &amp; Special Requests</span>
                    <span className="sm:hidden">Concierge</span>
                  </h3>
                  <p
                    className="font-body text-[#c9c9ba] hidden sm:block mt-2 max-w-md"
                    style={{ fontSize: "13px", lineHeight: 1.55 }}
                  >
                    Custom itineraries, multi-stop events, security details, private
                    aviation coordination — or anything bespoke. Tell us what you need.
                  </p>
                  <p
                    className="font-body text-[#c9c9ba] sm:hidden leading-snug mt-2"
                    style={{ fontSize: "12px" }}
                  >
                    Bespoke arrangements — tell us what you need.
                  </p>
                  <span
                    className="inline-flex items-center gap-1.5 mt-3 sm:mt-5 font-display font-bold uppercase tracking-[0.14em] text-[#d6d400]"
                    style={{ fontSize: "clamp(10px, 1vw, 13px)" }}
                    data-testid="concierge-cta"
                  >
                    <span className="hidden sm:inline">Contact Concierge</span>
                    <span className="sm:hidden">Contact</span>
                    <ArrowRight size={13} strokeWidth={2} />
                  </span>
                </div>
              </div>
            </button>
          </Reveal>
        </div>
      </div>

      <style>{`
        .cc-card {
          transition: transform 260ms ease-out, box-shadow 260ms ease-out;
          background: transparent;
          cursor: pointer;
        }
        .cc-card:hover {
          transform: translateY(-4px);
          box-shadow: 0 14px 40px rgba(0,0,0,0.55), 0 0 26px rgba(214,212,0,0.18);
        }
        .cc-card-disabled {
          cursor: not-allowed;
          opacity: 0.52;
          filter: grayscale(0.35);
        }
        .cc-card-disabled:hover {
          transform: none;
          box-shadow: none;
        }
        .cc-card:focus-visible {
          outline: 2px solid #d6d400;
          outline-offset: 3px;
        }
      `}</style>
    </section>
  );
}
